import moment from "moment";

/**
 * Formats a given date into a specified format.
 *
 * @param {any} date - The date to be formatted. This can be a Date object, string, or any value that Moment.js can parse.
 * @param {any} dateFormat - The format string to use for formatting the date. This should be a valid Moment.js format string.
 * @returns {string} - The formatted date as a string.
 *
 * @example
 * // Format a date object
 * const formattedDate = formatDate(new Date(), 'MMMM D, YYYY');
 * console.log(formattedDate); // Output: "July 13, 2024"
 *
 * @example
 * // Format a date string
 * const formattedDate = formatDate('2024-07-13', 'MMMM D, YYYY');
 * console.log(formattedDate); // Output: "July 13, 2024"
 */
export const formatDate = (date: any, dateFromat: any) => {
  return moment(date).format(dateFromat);
};

/**
 * Formats a given Unix timestamp into a specified date format.
 *
 * @param {any} timestamp - The Unix timestamp to be formatted. This should be a value that Moment.js can parse as a Unix timestamp.
 * @param {any} dateFormat - The format string to use for formatting the timestamp. This should be a valid Moment.js format string.
 * @returns {string} - The formatted date as a string.
 *
 * @example
 * // Format a Unix timestamp
 * const formattedDate = formatTimeStamp(1721145600, 'MMMM D, YYYY');
 * console.log(formattedDate); // Output: "July 13, 2024"
 *
 * @example
 * // Format a Unix timestamp with a different format
 * const formattedDate = formatTimeStamp(1721145600, 'YYYY-MM-DD');
 * console.log(formattedDate); // Output: "2024-07-13"
 */
export const formatTimeStamp = (timestamp: any, dateFromat: any) => {
  return moment.unix(timestamp).format(dateFromat);
};

export const formatDateTime = (timestamp: any, dateFromat: any) => {
  return moment(timestamp).format(dateFromat);
};

export const formatDateToISO = (date: Date) => {
  return moment(date).format("YYYY-MM-DD HH:mm:ss");
};

/**
 * Calculates the time difference from a given date and returns a human-readable string
 * in the format "upload X [unit] ago" using the biggest appropriate unit.
 *
 * @param {string | Date} date - The date to calculate time difference from
 * @returns {string} - A string like "upload 2 hours ago" or "upload 3 days ago"
 *
 * @example
 * getTimeAgo(new Date(Date.now() - 3600000)) // "upload 1 hour ago"
 * getTimeAgo(new Date(Date.now() - 86400000)) // "upload 1 day ago"
 */
export const getTimeAgo = (date: string | Date): string => {
  if (!date) return "upload -";

  const now = moment();
  const past = moment(date);
  const diffInSeconds = now.diff(past, "seconds");

  if (diffInSeconds < 60) {
    return `upload ${diffInSeconds} ${
      diffInSeconds === 1 ? "second" : "seconds"
    } ago`;
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `upload ${diffInMinutes} ${
      diffInMinutes === 1 ? "minute" : "minutes"
    } ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `upload ${diffInHours} ${diffInHours === 1 ? "hour" : "hours"} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return `upload ${diffInDays} ${diffInDays === 1 ? "day" : "days"} ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `upload ${diffInMonths} ${
      diffInMonths === 1 ? "month" : "months"
    } ago`;
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  return `upload ${diffInYears} ${diffInYears === 1 ? "year" : "years"} ago`;
};
